import { useState, useEffect } from 'react';
import { assinaturasService, type Assinatura } from '../services/assinaturas.service';

interface UseActiveSubscriptionReturn {
    subscription: Assinatura | null;
    isLoading: boolean;
    isFeatureIncluded: (featureKey: string) => boolean;
    refresh: () => Promise<void>;
}

export function useActiveSubscription(): UseActiveSubscriptionReturn {
    const [subscription, setSubscription] = useState<Assinatura | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const loadSubscription = async () => {
        setIsLoading(true);
        try {
            // Fetch subscriptions with pagination to find the active one
            const response = await assinaturasService.findAll({ page: 1, limit: 10 });
            const now = new Date();

            const activeSub = response.data.find((sub) => {
                const isStatusActive = sub.status === 'ACTIVE';
                const isNotCanceled = !sub.canceled_at;
                const isNotExpired = !sub.expires_at || new Date(sub.expires_at) > now;
                return isStatusActive && isNotCanceled && isNotExpired;
            });

            setSubscription(activeSub || null);
        } catch (error) {
            console.error('❌ Erro ao verificar assinatura ativa:', error);
            setSubscription(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadSubscription();
    }, []);

    const isFeatureIncluded = (featureKey: string): boolean => {
        if (!subscription?.plan?.features) return false;

        // Find the feature by key
        const feature = subscription.plan.features.find(f => f.feature_key === featureKey);

        // Return true only if feature exists AND includes is true
        // If feature doesn't exist or is not included, return false
        return feature?.included || false;
    };

    return {
        subscription,
        isLoading,
        isFeatureIncluded,
        refresh: loadSubscription
    };
}
